import type { Map as MlMap } from "maplibre-gl";

/** GISTDA flood-extent raster layers (satellite), proxied via /api/gistda/tile. */
export type GistdaWindow = "3days" | "7days" | "30days";
const WINDOWS: GistdaWindow[] = ["3days", "7days", "30days"];
const added = new Set<GistdaWindow>();
let allZoom = false;      // "ภาพดาวเทียมทั้งหมด" — show at every zoom
let overviewActive = false; // country button keeps the selected flood window visible
let radiusActive = false; // a point+radius is chosen → show from zoom 6 (item 2)

function minZoomNow(): number { return allZoom || overviewActive ? 0 : radiusActive ? 6 : 9; }
function applyMinZoom(map: MlMap): void {
  const mz = minZoomNow();
  for (const w of WINDOWS) { const id = layerId(w); if (map.getLayer(id)) map.setLayerZoomRange(id, mz, 24); }
}

/** Toggle whether the GISTDA raster shows at country zoom (default off: ≥9 only). */
export function setGistdaAllZoom(map: MlMap, on: boolean): void { allZoom = on; applyMinZoom(map); }
/** Country view overrides only the zoom gate, never the selected window or the user toggle. */
export function setGistdaOverview(map: MlMap, on: boolean): void { overviewActive = on; applyMinZoom(map); }
/** With a chosen point+radius, show the satellite flood from zoom 6 (not just ≥9). */
export function setGistdaRadiusActive(map: MlMap, on: boolean): void { radiusActive = on; applyMinZoom(map); }

function layerId(w: GistdaWindow): string { return `gistda-flood-${w}`; }

/**
 * v0.9.10b (owner decision): every window shows the SAME colour on screen, both modes. GISTDA serves 7 วัน as solid
 * rgb(0,138,197) and 3 / 30 วัน semi-transparent (alpha ≈ 0.73, median 0.75) in rgb(36,81,179) — measured
 * alpha-weighted over 3 tiles on 7 ต.ค. 2569. The 7-day look is the one users know (≈ rgb(31,36,255) after its
 * filter). 3 / 30 วัน get their own filter, solved with MapLibre's raster shader math (hue spin → saturation →
 * contrast → brightness): rgb(36,81,179) → rgb(23,27,245) (its opaque pixels rgb(45,101,222) → rgb(29,35,255)),
 * and opacity 0.96 so 0.96 × 0.75 ≈ the 7-day 0.72 × 1.
 * Gate: scripts/check-gistda-color.mjs (raw tiles from production through each layer's live paint + on-screen pixels).
 */
export const GISTDA_PAINT: Record<GistdaWindow, { hue: number; saturation: number; contrast: number; brightnessMax: number; opacity: number }> = {
  "7days": { hue: 42, saturation: 0.05, contrast: 0.18, brightnessMax: 1, opacity: 0.72 },
  "3days": { hue: 17, saturation: 0.425, contrast: 0, brightnessMax: 1, opacity: 0.96 },
  "30days": { hue: 17, saturation: 0.425, contrast: 0, brightnessMax: 1, opacity: 0.96 },
};

/** First label (symbol) layer of the current basemap, so a raster can be inserted
 *  beneath the road/place labels. */
function firstSymbolLayerId(map: MlMap): string | undefined {
  for (const l of map.getStyle().layers ?? []) if (l.type === "symbol") return l.id;
  return undefined;
}

function ensure(map: MlMap, w: GistdaWindow): void {
  if (added.has(w)) return;
  const src = `gistda-src-${w}`;
  if (!map.getSource(src)) {
    map.addSource(src, {
      type: "raster",
      tiles: [`${location.origin}/api/gistda/tile/${w}/{z}/{x}/{y}`],
      tileSize: 256,
      attribution: 'น้ำท่วม: <a href="https://www.gistda.or.th/" target="_blank" rel="noopener">GISTDA</a>',
    });
  }
  // insert the satellite raster BELOW the basemap's labels (road/place names stay
  // readable on top of the flood) — v0.8.2 item 4. Fall back to below our rivers.
  const before = firstSymbolLayerId(map) ?? (map.getLayer("rivers-glow") ? "rivers-glow" : undefined);
  map.addLayer({
    id: layerId(w), type: "raster", source: src, layout: { visibility: "none" },
    // default: subtle (0.45) and only at local zoom (≥9) so the satellite layer
    // never covers the whole map as scattered blue. "ภาพดาวเทียมทั้งหมด" (advanced)
    // drops the minzoom to show the country-wide extent.
    minzoom: minZoomNow(),
    paint: {
      // brighter blue + 'nearest' so low-zoom flood reads as an area, not dust.
      // The radius donut mask dims everything outside the circle, so this stays bright.
      "raster-opacity": GISTDA_PAINT[w].opacity,
      "raster-resampling": "nearest",
      "raster-hue-rotate": GISTDA_PAINT[w].hue,
      "raster-saturation": GISTDA_PAINT[w].saturation,
      "raster-contrast": GISTDA_PAINT[w].contrast,
      "raster-brightness-max": GISTDA_PAINT[w].brightnessMax,
    },
  }, before);
  added.add(w);
}

export function addGistdaLayers(map: MlMap): void {
  for (const w of WINDOWS) ensure(map, w);
}

/** Show one window's flood extent (or hide all when null). */
export function setGistdaWindow(map: MlMap, window: GistdaWindow | null): void {
  for (const w of WINDOWS) {
    ensure(map, w);
    const id = layerId(w);
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", w === window ? "visible" : "none");
  }
}
